/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { uniqBy, sort, map } from '@ember/object/computed';
import { service } from '@ember/service';
import GradingMethod from '@linc-technologies/linced-common/enums/grading-method';
import moment from 'moment';
import PersonType from '@linc-technologies/linced-common/enums/person-type';

export default class GradeHistoryTable extends Component {
  @action
  setAuditReason(value) {
    this.auditReason = value;
  }

  @tracked
  auditReason;

  @service
  session;

  @service
  paperToaster;

  @service
  permissions;

  GradingMethod = GradingMethod;
  PersonType = PersonType;
  @tracked editable = false;

  @computed('editable', 'session.scopes.[]')
  get canEdit() {
    return (
      this.editable &&
      this.permissions.meet('urn:linced:organisation:role:admin')
    );
  }

  editing = false;
  @tracked
  deleteDialogOpen = false;

  // rawDateAssessorTypes contains a list of dates extracted from all grades
  // Includes a concatenated dateAssessorType, so we potentially have
  // more than one row per date.
  @map('grades', function (grade) {
    let date = moment.unix(grade.get('createTime')).format('YYYY-MM-DD');
    let assessorType = grade.get('assessorType');
    let dateAssessorType = date + '|' + assessorType;
    return {
      date,
      assessorType,
      dateAssessorType,
    };
  })
  rawDateAssessorTypes;

  // uniqueDateAssessorTypes contains a list of unique dates from rawDateAssessorTypes
  @uniqBy('rawDateAssessorTypes', 'dateAssessorType')
  uniqueDateAssessorTypes;

  // dateAssessorTypes contains a sorted list of dates from uniqueDateAssessorTypes
  @sort('uniqueDateAssessorTypes', function (a, b) {
    if (a.dateAssessorType < b.dateAssessorType) {
      return 1;
    } else if (a.dateAssessorType > b.dateAssessorType) {
      return -1;
    }

    return 0;
  })
  dateAssessorTypes;

  @tracked
  afterDelete;

  @tracked
  deleteInProgress;

  @tracked
  gradeToDelete;

  refreshGrades() {}

  @action
  doNothing() {}

  @action
  findGrade(module, dateAssessorType) {
    let moduleGrades = this.grades
      .filter((grade) => grade.module_id === module.id)
      .filter((grade) => grade.assessorType === dateAssessorType.assessorType);
    if (moduleGrades && moduleGrades.length) {
      let dateGrades = moduleGrades.filter(function (grade) {
        return (
          moment.unix(grade.get('createTime')).format('YYYY-MM-DD') ===
          dateAssessorType.date
        );
      });

      if (dateGrades && dateGrades.length) {
        return dateGrades
          .slice()
          .sort((a, b) =>
            a.createTime > b.createTime
              ? 1
              : a.createTime < b.createTime
                ? -1
                : 0,
          )
          .at(-1);
      }
    }

    return null;
  }

  // NOTE: named differently from the `refreshGrades` closure-action default
  // above, which it invokes. In the classic class the two lived in separate
  // namespaces (component property vs `actions` hash); as sibling class members
  // the action would shadow the property and recurse into itself.
  @action
  handleRefreshGrades() {
    this.refreshGrades();
  }

  @action
  deleteGrade(gradeToDelete, afterDelete) {
    this.gradeToDelete = gradeToDelete;
    this.deleteDialogOpen = true;
    this.afterDelete = afterDelete;
  }

  @action
  confirmDeleteGrade(proceed) {
    if (proceed) {
      let toaster = this.paperToaster;
      let entity = this.gradeToDelete;
      entity.set('auditReason', this.auditReason);

      return new Promise((resolve, reject) => {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Delete record (locally + pushed to server).
        return entity
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            entity.rollbackAttributes();
            reject(err);
          });
      })
        .then(() => {
          // Tidy up / close dialog.
          this.deleteDialogOpen = false;
          toaster.show(`Grade deleted successfully.`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
          if (this.afterDelete) {
            this.afterDelete();
          }
        })
        .catch(() => {
          toaster.show(`Error deleting grade.`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        })
        .then(() => {
          this.deleteInProgress = false;
          this.afterDelete = null;
        });
    } else {
      this.deleteDialogOpen = false;
      this.deleteInProgress = false;
      this.gradeToDelete = null;
    }
  }
}
